import { Skeleton } from './ui/skeleton';

export type Column<T> = {
  key: string;
  header: React.ReactNode;
  cell: (row: T) => React.ReactNode;
  className?: string;
};

type Props<T> = {
  rows: T[] | undefined;
  columns: Column<T>[];
  isLoading?: boolean;
  rowKey: (row: T) => string | number;
  onRowClick?: (row: T) => void;
  emptyMessage?: React.ReactNode;
};

export function DataTable<T>({
  rows,
  columns,
  isLoading,
  rowKey,
  onRowClick,
  emptyMessage,
}: Props<T>) {
  if (isLoading) {
    return (
      <div className="space-y-2" data-testid="datatable-loading">
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-10 w-full" />
        ))}
      </div>
    );
  }

  if (!rows || rows.length === 0) {
    return (
      <div className="flex h-40 items-center justify-center rounded-md border border-dashed text-sm text-muted-foreground" data-testid="datatable-empty">
        {emptyMessage ?? 'No results.'}
      </div>
    );
  }

  return (
    // overflow-x-auto (not overflow-hidden) so a wide table — many columns on
    // a 1080p screen — scrolls WITHIN this card instead of forcing the whole
    // page to scroll sideways. min-w-full keeps the table filling the card when
    // it's narrow enough to fit. See PageLayout's min-w-0 on the content column.
    <div className="overflow-x-auto rounded-md border">
      <table className="w-full min-w-full text-sm" data-testid="datatable">
        <thead className="bg-muted/40">
          <tr>
            {columns.map((c) => (
              <th key={c.key} className={`px-3 py-2 text-left font-medium text-muted-foreground ${c.className ?? ''}`}>
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr
              key={rowKey(row)}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              // Zebra striping + slightly stronger hover so high-density
              // rows scan cleanly. Even rows get a very light tint
              // (~3% of muted); hover bumps to a fuller muted tone so
              // interactivity is obvious. Border-t on every row gives a
              // subtle divider without overwhelming the zebra.
              className={[
                'border-t border-border/60',
                idx % 2 === 1 ? 'bg-muted/30' : '',
                onRowClick ? 'cursor-pointer hover:bg-muted/70' : '',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {columns.map((c) => (
                <td key={c.key} className={`px-3 py-2 ${c.className ?? ''}`}>
                  {c.cell(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
